/**
 * Shared video URL helpers used by the Educational Hub list and the
 * dashboard's video modal, so both play links and uploads the same way.
 */

/**
 * Turns any YouTube watch/share/shorts/embed URL into an embeddable URL.
 * Returns null when the URL is not a recognisable YouTube link.
 */
export const toYouTubeEmbed = (rawUrl: string): string | null => {
  if (!rawUrl) return null;
  try {
    const url = new URL(rawUrl.trim());
    const host = url.hostname.replace(/^www\./, "");
    let videoId = "";

    if (host === "youtu.be") {
      videoId = url.pathname.slice(1);
    } else if (host.endsWith("youtube.com")) {
      if (url.pathname === "/watch") {
        videoId = url.searchParams.get("v") || "";
      } else if (url.pathname.startsWith("/embed/")) {
        videoId = url.pathname.split("/embed/")[1];
      } else if (url.pathname.startsWith("/shorts/")) {
        videoId = url.pathname.split("/shorts/")[1];
      }
    }

    videoId = (videoId || "").split("/")[0].split("?")[0];
    return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
  } catch {
    return null;
  }
};

export interface PlayableVideoLike {
  video?: { url?: string } | string | null;
  videoLink?: string | null;
  videolink?: string | null;
}

/** Pulls the playable URL out of a record, whether linked or uploaded. */
export const getPlayableSource = (
  it: PlayableVideoLike
): { type: "youtube" | "file"; url: string } | null => {
  const link = it.videoLink || it.videolink || "";
  if (link) {
    const embed = toYouTubeEmbed(link);
    if (embed) return { type: "youtube", url: embed };
    // A non-YouTube link is still worth trying in a plain player.
    return { type: "file", url: link };
  }

  // Uploaded file: `video` may be an object, a JSON array string, or a path.
  const raw = it.video;
  let fileUrl = "";
  if (raw && typeof raw === "object") {
    fileUrl = raw.url || "";
  } else if (typeof raw === "string" && raw && raw !== "[]") {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length) {
        fileUrl = typeof parsed[0] === "string" ? parsed[0] : parsed[0]?.url || "";
      } else if (typeof parsed === "string") {
        fileUrl = parsed;
      }
    } catch {
      fileUrl = raw;
    }
  }
  return fileUrl ? { type: "file", url: fileUrl } : null;
};
